import { ContentType } from '@amzn/awscat-pattern-library-service-model';

import { IMplServiceClient } from '../../api/IMplServiceClient';
import { log } from '../../loggers/Logger';
import { AsyncAdhocMigration } from '../AdhocMigration';
import { ContentEditorService } from '../ContentEditorService';
import { addMissingPatternSections } from '../SectionOperations';

/**
 * In older versions of APG, the Pattern template had fewer sections.
 * Drafts created against those older templates therefore lack sections
 * that the current template requires. This migration fetches the current
 * template and stages the missing sections at their template-ordered
 * indices.
 */
export class MissingSectionsMigration implements AsyncAdhocMigration {
  readonly kind = 'async' as const;
  readonly name = 'missing-pattern-sections';

  private logger = log.child({ class: this.constructor.name, migration: this.name });

  private readonly _mpl: IMplServiceClient;

  constructor(mpl: IMplServiceClient) {
    this._mpl = mpl;
  }

  /**
   * Fetch the current template and stage any sections missing from the draft.
   *
   * @throws {ServiceCallError} when the backend call fails.
   */
  async run(editor: ContentEditorService): Promise<void> {
    // Guard: editor must be open and editable before we do anything.
    const initialHandle = editor.loadedDocumentHandle;
    if (!initialHandle || !editor.isEditable) {
      this.logger.info('Skipping: editor is not open in an editable mode');
      return;
    }
    const contentId = initialHandle.contentId;

    // Guard: only applies to Pattern content types.
    const initialContent = editor.content;
    if (!initialContent) {
      this.logger.info({ contentId }, 'Skipping: editor has no content');
      return;
    }
    if (initialContent.contentMetadata.contentType !== ContentType.Pattern) {
      this.logger.info(
        { contentId, contentType: initialContent.contentMetadata.contentType },
        'Skipping: content is not a Pattern',
      );
      return;
    }

    // We need a templateId to know which sections are expected.
    const templateId = initialContent.contentTypeMetadata?.templateId;
    if (!templateId) {
      this.logger.info({ contentId }, 'Skipping: content has no templateId');
      return;
    }

    // Fetch the current template definition from the server.
    const response = await this._mpl.getContentTemplate(templateId);
    this.logger.info({ contentId, templateId }, 'Fetched template');

    // Re-read after the await: editor may have closed or switched contents.
    const handle = editor.loadedDocumentHandle;
    if (!handle || handle.contentId !== contentId) {
      this.logger.info(
        { contentId, currentContentId: handle?.contentId },
        'Skipping stage: editor closed or switched contents during fetch',
      );
      return;
    }
    if (!editor.isEditable) {
      this.logger.info({ contentId }, 'Skipping stage: editor is no longer editable');
      return;
    }
    const currentContent = editor.content;
    if (!currentContent) {
      this.logger.info({ contentId }, 'Skipping stage: editor has no content');
      return;
    }

    // Compute and stage missing sections (no-op if content already has them all).
    const updated = addMissingPatternSections(currentContent, response.content);
    if (updated === currentContent) {
      this.logger.info({ contentId }, 'No missing sections; nothing to stage');
      return;
    }

    editor.stageEdit(updated);
    this.logger.info({ contentId, templateId }, 'Staged missing sections');
  }
}
